import { useState, useEffect } from 'react';
import StepCard from './components/StepCard';
import StepModal from './components/StepModal';
import RewardModal from './components/RewardModal';
import ManifestModal from './components/ManifestModal';
import { steps as initialSteps } from '../../mocks/steps';
import type { Step, StepAnswer } from '../../types/step';

export default function Home() {
  const [steps, setSteps] = useState<Step[]>([]);
  const [answers, setAnswers] = useState<Record<number, StepAnswer>>({});
  const [selectedStep, setSelectedStep] = useState<Step | null>(null);
  const [showReward, setShowReward] = useState(false);
  const [showManifest, setShowManifest] = useState(false);
  const [completedStep, setCompletedStep] = useState<Step | null>(null);

  useEffect(() => {
    const savedSteps = localStorage.getItem('steps');
    const savedAnswers = localStorage.getItem('answers');

    if (savedSteps) {
      setSteps(JSON.parse(savedSteps));
    } else {
      const initializedSteps = initialSteps.map((step, index) => ({
        ...step,
        isUnlocked: index === 0,
        isDone: false
      }));
      setSteps(initializedSteps);
      localStorage.setItem('steps', JSON.stringify(initializedSteps));
    }

    if (savedAnswers) {
      setAnswers(JSON.parse(savedAnswers));
    }
  }, []);

  const progress = (steps.filter(s => s.isDone).length / steps.length) * 100;

  const handleStepClick = (step: Step) => {
    if (step.isUnlocked) {
      setSelectedStep(step);
    }
  };

  const handleStepComplete = (stepId: number, stepAnswers: StepAnswer) => {
    const updatedSteps = steps.map((step, index) => {
      if (step.id === stepId) {
        return { ...step, isDone: true };
      }
      if (step.id === stepId + 1) {
        return { ...step, isUnlocked: true };
      }
      return step;
    });

    const updatedAnswers = { ...answers, [stepId]: stepAnswers };

    setSteps(updatedSteps);
    setAnswers(updatedAnswers);
    localStorage.setItem('steps', JSON.stringify(updatedSteps));
    localStorage.setItem('answers', JSON.stringify(updatedAnswers));

    const completed = updatedSteps.find(s => s.id === stepId);
    setCompletedStep(completed || null);
    setSelectedStep(null);
    setShowReward(true);
  };

  const handleRewardClose = () => {
    setShowReward(false);
    if (completedStep?.id === 15) {
      setShowManifest(true);
    }
  };

  const generateManifest = () => {
    const manifest = `Я — ${answers[15]?.responses[0] || '___'}

Моя ценность — ${answers[2]?.responses[0] || '___'}

Мой путь — ${answers[1]?.responses[0] || '___'}

Я больше не терплю — ${answers[4]?.responses[0] || '___'}

Я выбираю — ${answers[11]?.responses[0] || '___'}

Моя энергия — ${answers[6]?.responses[0] || '___'}

Мой голос — ${answers[7]?.responses[0] || '___'}

Я иду в — ${answers[13]?.responses[0] || '___'}`;

    return manifest;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-2 bg-gray-100 z-50">
        <div 
          className="h-full bg-gradient-to-r from-[#F9659C] to-[#ACE0EB] transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Header */}
      <div className="pt-12 pb-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Путь профессиональной силы
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-6">
            15 шагов к уверенности, голосу и внутренней опоре
          </p>
          <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-[#F9659C] text-xl"></i>
              {steps.filter(s => s.isDone).length} из 15 шагов
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-fire-fill text-[#ACE0EB] text-xl"></i>
              {Math.round(progress)}% пройдено
            </span>
          </div>
        </div>
      </div>

      {/* Steps Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((step) => (
            <StepCard
              key={step.id}
              step={step}
              onClick={() => handleStepClick(step)}
            />
          ))}
        </div>
      </div>

      {/* Modals */}
      {selectedStep && (
        <StepModal
          step={selectedStep}
          answers={answers[selectedStep.id]}
          onClose={() => setSelectedStep(null)}
          onComplete={handleStepComplete}
        />
      )}

      {showReward && completedStep && (
        <RewardModal
          step={completedStep}
          answers={answers[completedStep.id]}
          onClose={handleRewardClose}
        />
      )}

      {showManifest && (
        <ManifestModal
          manifest={generateManifest()}
          answers={answers}
          onClose={() => setShowManifest(false)}
        />
      )}
    </div>
  );
}
